import { z } from "zod";
import { ANALYTICS_DEFAULT_TIME_ZONE, ANALYTICS_RANGES, APP_DATA_AVAILABILITY, staffAnalyticsAccessSchema, } from "./staff-analytics.js";
// The Events tab contract (ADR 0017, decisions 5 and 9; CHT-2030): the events of ONE customer read back from the vendor on the server, one
// event with the visit it belongs to, a visit step by step, and the last visits for the Overview. CHT-2030 owns this file.
//
// STAFF ONLY and closed. A property is a closed value of the taxonomy (an enum label, a bucket label, a flag, a small whole number, an opaque
// id or a release tag): never a message, a name, an amount or a place. The server sends no sentence and no TRACKED / PLANNED badge: the admin
// app makes both from the event name. `@lafayette/schemas` may not import the domain, so the event list is written here and a parity test in
// `apps/api` fails when it differs from the taxonomy.
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/u;
const uuid = z.string().regex(UUID);
const isoDateTime = z.iso.datetime({ offset: false });
const count = z.number().int().min(0).max(1_000_000_000);
const seconds = z.number().int().min(0).max(1_000_000_000);
export const ANALYTICS_EVENT_NAMES = [
    "app_opened",
    "session_started",
    "screen_viewed",
    "session_ended",
    "activation_step_completed",
    "concierge_run_completed",
    "recommendation_viewed",
    "save_added",
    "save_removed",
    "request_created",
    "request_state_changed",
    "itinerary_created",
    "itinerary_edited",
    "handoff_requested",
    "handoff_accepted",
    "action_completed",
    "notification_opened",
    "admin_operation_performed",
    "experiment_exposed",
    "payment_requested",
    "payment_started",
    "payment_succeeded",
    "payment_failed",
    "payment_expired",
    "payment_refunded",
];
export const ANALYTICS_EVENT_AREAS = [
    "app",
    "requests",
    "payments",
    "trips",
    "conversations",
    "notifications",
];
/**
 * The events that are in the taxonomy but that nothing sends yet (`docs/engineering/analytics.md`, "Which events are wired today"). The admin app
 * draws PLANNED for them and TRACKED for every other event. A parity test in `apps/api` keeps this list equal to the domain's.
 */
export const ANALYTICS_PLANNED_EVENTS = [
    "activation_step_completed",
    "notification_opened",
];
export const ANALYTICS_PLATFORMS = ["ios", "android"];
export const ANALYTICS_ENTRIES = ["cold_start", "resume", "deep_link", "notification"];
/** The most steps one visit answer holds. A visit with more is cut and says so (`stepsTruncated`). */
export const ANALYTICS_VISIT_STEPS_MAX = 100;
export const ANALYTICS_EVENT_PAGE_DEFAULT = 50;
export const ANALYTICS_EVENT_PAGE_MAX = 100;
/** The visits and steps of the Overview card ("the last 3 visits, the first 5 steps of each"). */
export const ANALYTICS_RECENT_VISITS = 3;
export const ANALYTICS_RECENT_STEPS = 5;
const ianaZone = z
    .string()
    .min(1)
    .max(64)
    .regex(/^[A-Za-z][A-Za-z0-9_+\-/]*$/u);
const pageCursor = z
    .string()
    .min(1)
    .max(512)
    .regex(/^[A-Za-z0-9_-]+$/u);
// --- the requests --------------------------------------------------------------------------------------------------------------------
/** The window of the event routes: a range name, and for a zoom both `from` and `to`. */
const windowFields = {
    range: z.enum(ANALYTICS_RANGES).default("30d"),
    from: isoDateTime.optional(),
    to: isoDateTime.optional(),
    tz: ianaZone.default(ANALYTICS_DEFAULT_TIME_ZONE),
};
const bothOrNeither = (value) => (value.from === undefined) === (value.to === undefined);
export const staffAnalyticsEventsQuerySchema = z
    .strictObject({
    ...windowFields,
    area: z.enum(ANALYTICS_EVENT_AREAS).optional(),
    event: z.enum(ANALYTICS_EVENT_NAMES).optional(),
    visitId: uuid.optional(),
    cursor: pageCursor.optional(),
    limit: z.coerce
        .number()
        .int()
        .min(1)
        .max(ANALYTICS_EVENT_PAGE_MAX)
        .default(ANALYTICS_EVENT_PAGE_DEFAULT),
})
    .refine(bothOrNeither);
export const staffAnalyticsEventSummaryQuerySchema = z
    .strictObject(windowFields)
    .refine(bothOrNeither);
// --- the answers ---------------------------------------------------------------------------------------------------------------------
const propertyKey = z.string().regex(/^[a-z][a-z0-9_]{0,63}$/u);
const propertyValue = z.union([z.string().max(64), z.number().finite(), z.boolean()]);
/** The closed properties of one event, after the forbidden-data wall of the server. At most 24 keys. */
export const staffAnalyticsEventPropertiesSchema = z
    .record(propertyKey, propertyValue)
    .refine((value) => Object.keys(value).length <= 24);
const eventName = z.enum(ANALYTICS_EVENT_NAMES);
const availability = z.enum(APP_DATA_AVAILABILITY);
export const staffAnalyticsEventRowSchema = z.strictObject({
    uuid,
    event: eventName,
    area: z.enum(ANALYTICS_EVENT_AREAS),
    timestamp: isoDateTime,
    /** The visit of this customer that the event belongs to. `null` when the event names none, or names one that is not a visit of this customer. */
    visitId: uuid.nullable(),
    properties: staffAnalyticsEventPropertiesSchema,
});
/** `GET .../events`. Newest first; the cursor is opaque and signed. Rows are empty unless `availability` is `AVAILABLE`. */
export const staffAnalyticsEventPageSchema = z.strictObject({
    access: staffAnalyticsAccessSchema,
    availability,
    rows: z.array(staffAnalyticsEventRowSchema).max(ANALYTICS_EVENT_PAGE_MAX),
    nextCursor: pageCursor.nullable(),
});
/** `GET .../events/summary`. `count` is `null` unless `availability` is `AVAILABLE`. */
export const staffAnalyticsEventSummarySchema = z.strictObject({
    access: staffAnalyticsAccessSchema,
    availability,
    count: count.nullable(),
});
const visitSummary = z.strictObject({
    visitId: uuid,
    startedAt: isoDateTime,
    endedAt: isoDateTime.nullable(),
    durationSeconds: seconds,
    startInferred: z.boolean(),
    platform: z.enum(ANALYTICS_PLATFORMS).nullable(),
    entry: z.enum(ANALYTICS_ENTRIES).nullable(),
    screenCount: count,
    eventCount: count,
});
const step = z.strictObject({
    uuid,
    event: eventName,
    timestamp: isoDateTime,
    properties: staffAnalyticsEventPropertiesSchema,
});
/** `GET .../events/:eventUuid`. `event` and `visit` are `null` unless `availability` is `AVAILABLE`. */
export const staffAnalyticsEventDetailSchema = z.strictObject({
    access: staffAnalyticsAccessSchema,
    availability,
    event: z
        .strictObject({
        uuid,
        event: eventName,
        area: z.enum(ANALYTICS_EVENT_AREAS),
        timestamp: isoDateTime,
        platform: z.enum(ANALYTICS_PLATFORMS).nullable(),
        appVersion: z.string().max(64).nullable(),
        visitId: uuid.nullable(),
        properties: staffAnalyticsEventPropertiesSchema,
    })
        .nullable(),
    /** Where the event sits in its visit: step `stepIndex` of `stepCount` (both start at 1). `null` when the event has no visit of this customer. */
    visit: z
        .strictObject({
        visitId: uuid,
        startedAt: isoDateTime,
        stepIndex: z.number().int().min(1).max(ANALYTICS_VISIT_STEPS_MAX),
        stepCount: z.number().int().min(1).max(ANALYTICS_VISIT_STEPS_MAX),
    })
        .nullable(),
});
/** `GET .../visits/:visitId`. Oldest step first (by the sequence number of the phone, never by time). */
export const staffAnalyticsVisitDetailSchema = z.strictObject({
    access: staffAnalyticsAccessSchema,
    availability,
    visit: visitSummary.nullable(),
    steps: z.array(step).max(ANALYTICS_VISIT_STEPS_MAX),
    stepsTruncated: z.boolean(),
});
/** `GET .../recent-visits`: the last visits and the first steps of each. `steps` is `null` for a visit whose events could not be read. */
export const staffAnalyticsRecentVisitsSchema = z.strictObject({
    access: staffAnalyticsAccessSchema,
    availability,
    visits: z
        .array(z.strictObject({
        visit: visitSummary,
        steps: z.array(step).max(ANALYTICS_RECENT_STEPS).nullable(),
    }))
        .max(ANALYTICS_RECENT_VISITS),
    /** The events of the customer in the retention window, for "Open all N events". `null` when it could not be counted. */
    eventCount: count.nullable(),
});
